import { Construction, ArrowLeft } from "lucide-react";
import { Link } from "wouter";
import { useLanguage } from "../contexts/LanguageContext";
import type { Language } from "../../../shared/const";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

interface Props {
  title: Record<Language, string>;
  description?: Record<Language, string>;
  phase?: string;
}

export function Placeholder({ title, description, phase = "Phase 5" }: Props) {
  const { t } = useLanguage();
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge variant="warning" size="sm">
            <Construction className="h-2.5 w-2.5" />
            {phase}
          </Badge>
          <span className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
            {t({ "zh-TW": "規劃中", "en-US": "Planned", "zh-CN": "规划中" })}
          </span>
        </div>
        <h1 className="font-display text-3xl font-medium tracking-tight">{t(title)}</h1>
        {description && (
          <p className="max-w-2xl text-[14px] leading-relaxed text-muted-foreground">
            {t(description)}
          </p>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="bg-grid bg-grid-fade absolute inset-x-0 h-32 opacity-30" />
        <CardContent className="relative space-y-4 p-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-full border border-border bg-background">
            <Construction className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="space-y-1">
            <p className="font-display text-lg font-medium">
              {t({
                "zh-TW": "頁面骨架已就位",
                "en-US": "Skeleton in place",
                "zh-CN": "页面骨架已就位",
              })}
            </p>
            <p className="max-w-md text-[13px] leading-relaxed text-muted-foreground">
              {t({
                "zh-TW": `導覽與權限守門已完成。實作（Repository → Service → Controller → UI）將在 ${phase} 由各 sub-agent 分層補完。`,
                "en-US": `Navigation and access control are wired. Implementation (Repository → Service → Controller → UI) lands in ${phase}.`,
                "zh-CN": `导览与权限守门已完成。实作将在 ${phase} 由各 sub-agent 分层补完。`,
              })}
            </p>
          </div>
          <Link href="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-3.5 w-3.5" />
              {t({ "zh-TW": "回工作台", "en-US": "Back to dashboard", "zh-CN": "回工作台" })}
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
